/**
 * Editorial homepage cart popover: live cart data, selection, qty, remove, variant edit.
 */
(function () {
  const CART_JS_URL = '/cart.js';

  /**
   * @param {string} text
   * @returns {string}
   */
  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {number} cents
   * @param {string} format
   * @param {string} currency
   * @returns {string}
   */
  function formatMoney(cents, format, currency) {
    const amount = (cents / 100).toFixed(2);
    const parts = amount.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = withCommas + '.' + parts[1];

    if (format && format.indexOf('{{') !== -1) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/gi, formatted)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, withCommas)
        .replace(/\{\{\s*currency\s*\}\}/gi, currency || '');
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (error) {
      return formatted;
    }
  }

  /**
   * @returns {Promise<object>}
   */
  function fetchCart() {
    return fetch(CART_JS_URL, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Cart fetch failed');
      }

      return response.json();
    });
  }

  /**
   * @param {string} changeUrl
   * @param {string} lineKey
   * @param {number} quantity
   * @returns {Promise<object>}
   */
  function changeCartLine(changeUrl, lineKey, quantity) {
    const url = changeUrl || '/cart/change.js';

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity,
      }),
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (!response.ok || payload.status) {
          const error = new Error(payload.description || payload.message || 'Cart update failed');
          error.description = payload.description || '';
          error.status = payload.status || response.status;
          throw error;
        }

        return payload;
      });
    });
  }

  /**
   * @param {string} template
   * @param {number} quantity
   * @returns {string}
   */
  function formatCartLabelWithQuantity(template, quantity) {
    const safeQty = Math.max(0, quantity);
    const text = template || 'Not enough stock. You can only add [quantity] of this item.';

    return text
      .replace(/\{\{\s*quantity\s*\}\}/gi, String(safeQty))
      .replace(/\[quantity\]/gi, String(safeQty));
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement | null}
   */
  function getCartPanelNotice(panel) {
    const notice = panel.querySelector('[data-cart-panel-notice]');
    return notice instanceof HTMLElement ? notice : null;
  }

  /**
   * @param {HTMLElement} panel
   */
  function hideCartPanelNotice(panel) {
    const notice = getCartPanelNotice(panel);
    if (!notice) {
      return;
    }

    notice.hidden = true;
    notice.classList.remove('is-visible');
    notice.textContent = '';
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} message
   */
  function showCartPanelNotice(panel, message) {
    const notice = getCartPanelNotice(panel);
    if (!notice || !message) {
      return;
    }

    notice.textContent = message;
    notice.hidden = false;
    notice.classList.add('is-visible');

    if (panel._cartNoticeTimer) {
      window.clearTimeout(panel._cartNoticeTimer);
    }

    panel._cartNoticeTimer = window.setTimeout(function () {
      hideCartPanelNotice(panel);
    }, 6000);
  }

  /**
   * @param {HTMLElement} panel
   * @param {Error} error
   * @param {number} [availableQty]
   * @returns {string}
   */
  function getInventoryErrorMessage(panel, error, availableQty) {
    const template =
      panel.dataset.labelInventoryError ||
      'Not enough stock. You can only add [quantity] of this item.';

    if (error && error.description) {
      return error.description;
    }

    if (error && error.message && error.message !== 'Cart update failed') {
      return error.message;
    }

    if (Number.isFinite(availableQty) && availableQty > 0) {
      return formatCartLabelWithQuantity(template, availableQty);
    }

    return template.replace(/\{\{\s*quantity\s*\}\}/gi, '').replace(/\[quantity\]/gi, '').trim() ||
      'Not enough stock for this item.';
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} lineKey
   * @param {number} requestedQty
   * @param {object} cart
   * @returns {string}
   */
  function getInventoryLimitMessage(panel, lineKey, requestedQty, cart) {
    const line =
      cart && Array.isArray(cart.items)
        ? cart.items.find(function (entry) {
            return entry.key === lineKey;
          })
        : null;

    if (!line || requestedQty <= 0) {
      return '';
    }

    if (line.quantity < requestedQty) {
      return formatCartLabelWithQuantity(
        panel.dataset.labelInventoryError ||
          'Not enough stock. You can only add [quantity] of this item.',
        line.quantity
      );
    }

    return '';
  }

  /**
   * @param {object} cart
   */
  function dispatchCartUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          data: {
            itemCount: cart.item_count,
            source: 'editorial-cart-panel',
          },
        },
      })
    );
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} isLoading
   */
  function setPanelLoading(panel, isLoading) {
    const list = panel.querySelector('[data-editorial-cart-list]');
    if (!list) return;

    list.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      list.setAttribute('data-cart-loading', 'true');
    } else {
      list.removeAttribute('data-cart-loading');
    }
    list.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  /**
   * @param {HTMLElement} panel
   * @returns {string}
   */
  function getSelectedKey(panel) {
    return panel.dataset.selectedLineKey || '';
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} key
   */
  function setSelectedKey(panel, key) {
    if (key) {
      panel.dataset.selectedLineKey = key;
    } else {
      delete panel.dataset.selectedLineKey;
    }
  }

  /**
   * @param {object} item
   * @returns {string[]}
   */
  function getItemOptionValues(item) {
    if (Array.isArray(item.options_with_values) && item.options_with_values.length) {
      return item.options_with_values
        .map(function (option) {
          return option && option.value ? String(option.value).trim() : '';
        })
        .filter(Boolean);
    }

    const variantTitle = item.variant_title || '';
    if (!variantTitle || variantTitle === 'Default Title') {
      return [];
    }

    return variantTitle
      .split(' / ')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  function formatOptionHash(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed) {
      return '#—';
    }

    return trimmed.charAt(0) === '#' ? trimmed : '#' + trimmed;
  }

  /**
   * Shopify /products/{handle}.js returns options as strings or { name, values } objects.
   * @param {object} product
   * @param {number} optionIndex
   * @returns {string}
   */
  function getProductOptionName(product, optionIndex) {
    if (!product || !Array.isArray(product.options)) {
      return '';
    }

    const option = product.options[optionIndex];
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option.trim();
    }

    if (typeof option === 'object' && option.name) {
      return String(option.name).trim();
    }

    return '';
  }

  /**
   * @param {string[]} values
   * @param {string} currentValue
   * @returns {string}
   */
  function renderDropdownMenuOptions(values, currentValue) {
    return values
      .map(function (value) {
        const isSelected = value === currentValue;
        return (
          '<li role="none">' +
          '<button type="button" class="editorial-homepage__cart-variant-dropdown-option' +
          (isSelected ? ' is-selected' : '') +
          '" data-cart-dropdown-option data-value="' +
          escapeHtml(value) +
          '" role="option" aria-selected="' +
          (isSelected ? 'true' : 'false') +
          '">' +
          escapeHtml(value) +
          '</button>' +
          '</li>'
        );
      })
      .join('');
  }

  /**
   * @param {string[]} values
   * @param {string} currentValue
   * @param {number} optionIndex
   * @param {string} optionName
   * @returns {string}
   */
  function renderOptionSelect(values, currentValue, optionIndex, optionName) {
    const displayValue = currentValue || values[0] || '';
    const label = optionName || 'Option';

    return (
      '<div class="editorial-homepage__cart-variant-dropdown" data-cart-variant-dropdown data-option-index="' +
      optionIndex +
      '">' +
      '<select class="editorial-homepage__cart-variant-select editorial-homepage__cart-variant-select--native" data-cart-option-select data-option-index="' +
      optionIndex +
      '" tabindex="-1" aria-hidden="true">' +
      values
        .map(function (value) {
          return (
            '<option value="' +
            escapeHtml(value) +
            '"' +
            (value === displayValue ? ' selected' : '') +
            '>' +
            escapeHtml(value) +
            '</option>'
          );
        })
        .join('') +
      '</select>' +
      '<button type="button" class="editorial-homepage__cart-variant-dropdown-trigger" data-cart-dropdown-trigger aria-haspopup="listbox" aria-expanded="false" aria-label="' +
      escapeHtml(label) +
      '">' +
      '<span class="editorial-homepage__cart-variant-dropdown-value" data-cart-dropdown-value>' +
      escapeHtml(displayValue) +
      '</span>' +
      '<span class="editorial-homepage__cart-variant-dropdown-caret" aria-hidden="true"></span>' +
      '</button>' +
      '<ul class="editorial-homepage__cart-variant-dropdown-menu" data-cart-dropdown-menu role="listbox" aria-label="' +
      escapeHtml(label) +
      '">' +
      renderDropdownMenuOptions(values, displayValue) +
      '</ul>' +
      '</div>'
    );
  }

  /**
   * @param {HTMLElement} dropdown
   * @returns {string}
   */
  function ensureDropdownId(dropdown) {
    if (!dropdown.dataset.dropdownId) {
      dropdown.dataset.dropdownId =
        'cart-dd-' + String(Date.now()) + '-' + String(Math.random()).slice(2, 8);
    }

    return dropdown.dataset.dropdownId;
  }

  /**
   * @param {HTMLElement} dropdown
   * @returns {HTMLElement | null}
   */
  function findDropdownMenu(dropdown) {
    const dropdownId = dropdown.dataset.dropdownId;

    if (dropdownId) {
      const portaled = document.querySelector(
        '[data-cart-dropdown-menu][data-owner-dropdown-id="' + dropdownId + '"]'
      );

      if (portaled instanceof HTMLElement) {
        return portaled;
      }
    }

    const local = dropdown.querySelector('[data-cart-dropdown-menu]');
    return local instanceof HTMLElement ? local : null;
  }

  /**
   * @param {HTMLElement} dropdown
   * @param {HTMLElement} menu
   */
  function attachDropdownMenuToBody(dropdown, menu) {
    if (menu.dataset.portaled === 'true') {
      return;
    }

    const placeholder = document.createComment('cart-variant-menu');
    if (menu.parentNode) {
      menu.parentNode.insertBefore(placeholder, menu);
    }

    dropdown._menuPlaceholder = placeholder;
    menu.dataset.portaled = 'true';
    menu.dataset.ownerDropdownId = ensureDropdownId(dropdown);
    document.body.appendChild(menu);

    if (menu.dataset.cartMenuGuardBound !== 'true') {
      menu.dataset.cartMenuGuardBound = 'true';

      menu.addEventListener('click', function (event) {
        event.stopPropagation();
      });
    }
  }

  /**
   * @param {HTMLElement} dropdown
   * @param {HTMLElement} menu
   */
  function restoreDropdownMenu(dropdown, menu) {
    if (menu.dataset.portaled !== 'true') {
      return;
    }

    const placeholder = dropdown._menuPlaceholder;
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.insertBefore(menu, placeholder);
      placeholder.remove();
    } else {
      dropdown.appendChild(menu);
    }

    delete menu.dataset.portaled;
    delete menu.dataset.ownerDropdownId;
    delete dropdown._menuPlaceholder;
  }

  /**
   * @param {HTMLElement} dropdown
   * @param {HTMLElement} menu
   */
  function positionVariantDropdownMenu(dropdown, menu) {
    const trigger = dropdown.querySelector('[data-cart-dropdown-trigger]');

    if (!trigger || !menu || !menu.classList.contains('is-open')) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const maxMenuHeight = 148;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUp = spaceBelow < Math.min(maxMenuHeight, menu.scrollHeight || maxMenuHeight) && spaceAbove > spaceBelow;

    menu.style.position = 'fixed';
    menu.style.left = Math.max(viewportPadding, rect.left) + 'px';
    menu.style.width = Math.min(rect.width, window.innerWidth - viewportPadding * 2) + 'px';
    menu.style.zIndex = '100000';
    menu.style.maxHeight = Math.min(maxMenuHeight, openUp ? spaceAbove - gap : spaceBelow - gap) + 'px';

    if (openUp) {
      menu.style.top = 'auto';
      menu.style.bottom = window.innerHeight - rect.top + gap + 'px';
    } else {
      menu.style.bottom = 'auto';
      menu.style.top = rect.bottom + gap + 'px';
    }
  }

  /**
   * @param {HTMLElement} menu
   */
  function resetVariantDropdownMenuPosition(menu) {
    if (!menu) {
      return;
    }

    menu.style.position = '';
    menu.style.top = '';
    menu.style.bottom = '';
    menu.style.left = '';
    menu.style.width = '';
    menu.style.zIndex = '';
    menu.style.maxHeight = '';
  }

  /**
   * @param {HTMLElement} dropdown
   */
  function unbindVariantDropdownListeners(dropdown) {
    if (dropdown._repositionMenu) {
      window.removeEventListener('resize', dropdown._repositionMenu);
      delete dropdown._repositionMenu;
    }
  }

  /**
   * @param {HTMLElement} dropdown
   */
  function closeSingleCartVariantDropdown(dropdown) {
    dropdown.classList.remove('is-open');
    unbindVariantDropdownListeners(dropdown);

    const menu = findDropdownMenu(dropdown);
    const trigger = dropdown.querySelector('[data-cart-dropdown-trigger]');

    if (menu) {
      menu.classList.remove('is-open');
      resetVariantDropdownMenuPosition(menu);
      restoreDropdownMenu(dropdown, menu);
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} [exceptDropdown]
   */
  function closeCartVariantDropdowns(panel, exceptDropdown) {
    if (!panel) {
      return;
    }

    panel.querySelectorAll('[data-cart-variant-dropdown].is-open').forEach(function (dropdown) {
      if (exceptDropdown && dropdown === exceptDropdown) {
        return;
      }

      closeSingleCartVariantDropdown(dropdown);
    });
  }

  /**
   * @param {HTMLElement} dropdown
   */
  function openCartVariantDropdown(dropdown) {
    const panel = dropdown.closest('[data-editorial-cart-panel]');
    if (!panel) {
      return;
    }

    closeCartVariantDropdowns(panel, dropdown);
    ensureDropdownId(dropdown);

    const menu = findDropdownMenu(dropdown);
    const trigger = dropdown.querySelector('[data-cart-dropdown-trigger]');

    if (!menu || !trigger) {
      return;
    }

    dropdown.classList.add('is-open');
    attachDropdownMenuToBody(dropdown, menu);
    menu.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(function () {
      positionVariantDropdownMenu(dropdown, menu);
    });

    dropdown._repositionMenu = function () {
      const activeMenu = findDropdownMenu(dropdown);
      if (activeMenu) {
        positionVariantDropdownMenu(dropdown, activeMenu);
      }
    };
    window.addEventListener('resize', dropdown._repositionMenu);
  }

  /**
   * @param {HTMLElement} dropdown
   */
  function toggleCartVariantDropdown(dropdown) {
    if (dropdown.classList.contains('is-open')) {
      closeCartVariantDropdowns(dropdown.closest('[data-editorial-cart-panel]'));
    } else {
      openCartVariantDropdown(dropdown);
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} activeDropdown
   * @param {HTMLElement} optionBtn
   * @param {HTMLElement} menu
   */
  function applyCartDropdownOptionSelection(panel, activeDropdown, optionBtn, menu) {
    const item = activeDropdown.closest('[data-editorial-cart-item]');
    const row = activeDropdown.closest('[data-cart-option-row]');
    const select = row && row.querySelector('[data-cart-option-select]');
    const optionIndex = parseInt(activeDropdown.dataset.optionIndex, 10);
    const newValue = optionBtn.dataset.value || '';

    if (!item || !row || !select || Number.isNaN(optionIndex) || !newValue) {
      return;
    }

    if (select.value === newValue) {
      closeCartVariantDropdowns(panel);
      return;
    }

    select.value = newValue;

    const valueEl = activeDropdown.querySelector('[data-cart-dropdown-value]');
    if (valueEl) {
      valueEl.textContent = newValue;
    }

    menu.querySelectorAll('[data-cart-dropdown-option]').forEach(function (button) {
      const isSelected = button.dataset.value === newValue;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    closeCartVariantDropdowns(panel);
    handleVariantSelectChange(panel, item, optionIndex);
  }

  let cartDropdownDocumentCloseBound = false;

  function ensureCartDropdownDocumentClose() {
    if (cartDropdownDocumentCloseBound) {
      return;
    }

    cartDropdownDocumentCloseBound = true;

    document.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const optionBtn = target.closest('[data-cart-dropdown-option]');
      if (optionBtn) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const menu = optionBtn.closest('[data-cart-dropdown-menu]');
        const ownerId = menu && menu.dataset.ownerDropdownId;

        if (!menu || !ownerId) {
          return;
        }

        document.querySelectorAll('[data-editorial-cart-panel]').forEach(function (panel) {
          const dropdown = panel.querySelector(
            '[data-cart-variant-dropdown][data-dropdown-id="' + ownerId + '"]'
          );

          if (!dropdown) {
            return;
          }

          applyCartDropdownOptionSelection(panel, dropdown, optionBtn, menu);
        });

        return;
      }

      if (target.closest('[data-cart-variant-dropdown]') || target.closest('[data-cart-dropdown-menu]')) {
        return;
      }

      document.querySelectorAll('[data-editorial-cart-panel]').forEach(function (panel) {
        closeCartVariantDropdowns(panel);
      });
    }, true);

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') {
        return;
      }

      document.querySelectorAll('[data-editorial-cart-panel]').forEach(function (panel) {
        closeCartVariantDropdowns(panel);
      });
    });
  }

  /**
   * @param {string} url
   * @param {object} labels
   * @param {string} iconUrl
   * @returns {string}
   */
  /**
   * @param {object} labels
   * @param {string} iconUrl
   * @returns {string}
   */
  function renderDeleteButton(labels, iconUrl) {
    const iconMarkup = iconUrl
      ? '<img src="' + escapeHtml(iconUrl) + '" width="18" height="18" alt="" loading="lazy" decoding="async">'
      : '';

    return (
      '<button type="button" class="editorial-homepage__cart-remove-btn editorial-homepage__cart-cell-delete" data-cart-remove aria-label="' +
      escapeHtml(labels.remove) +
      '">' +
      iconMarkup +
      '</button>'
    );
  }

  function renderReplaceEditButton(labels, iconUrl, optionIndex) {
    const iconMarkup = iconUrl
      ? '<img src="' + escapeHtml(iconUrl) + '" width="18" height="18" alt="" loading="lazy" decoding="async">'
      : '';

    return (
      '<button type="button" class="editorial-homepage__cart-edit-btn" data-cart-variant-edit data-option-index="' +
      optionIndex +
      '" aria-label="' +
      escapeHtml(labels.edit) +
      '" aria-expanded="false">' +
      iconMarkup +
      '</button>'
    );
  }

  /**
   * @param {object} item
   * @returns {{ name: string, value: string, index: number }[]}
   */
  function getItemOptionsWithMeta(item) {
    if (Array.isArray(item.options_with_values) && item.options_with_values.length) {
      return item.options_with_values.map(function (option, index) {
        return {
          name: option && option.name ? String(option.name).trim() : '',
          value: option && option.value ? String(option.value).trim() : '',
          index: index,
        };
      });
    }

    return getItemOptionValues(item).map(function (value, index) {
      return {
        name: '',
        value: value,
        index: index,
      };
    });
  }

  /**
   * @param {object} item
   * @param {string} url
   * @param {object} labels
   * @param {string} iconUrl
   * @returns {string}
   */
  /**
   * @param {string} url
   * @returns {string}
   */
  function extractProductHandle(url) {
    if (!url) {
      return '';
    }

    try {
      const path = new URL(url, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      const match = String(url).match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    }
  }

  function renderOptionRows(item, labels, iconUrl) {
    const optionsMeta = getItemOptionsWithMeta(item);
    const rows = optionsMeta.length ? optionsMeta : [{ name: '', value: '—', index: 0 }];

    return (
      '<div class="editorial-homepage__cart-options editorial-homepage__cart-cell-options" data-cart-options>' +
      rows
        .map(function (meta) {
          return (
            '<div class="editorial-homepage__cart-option-row" data-cart-option-row data-option-index="' +
            meta.index +
            '">' +
            '<div class="editorial-homepage__cart-option-display" data-cart-option-display>' +
            '<span class="editorial-homepage__cart-option-value">' +
            escapeHtml(formatOptionHash(meta.value)) +
            '</span>' +
            renderReplaceEditButton(labels, iconUrl, meta.index) +
            '</div>' +
            '<div class="editorial-homepage__cart-variant-editor-row" data-cart-variant-editor-row hidden></div>' +
            '</div>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  /**
   * @param {number} quantity
   * @param {boolean} [isExpanded]
   * @returns {string}
   */
  function renderQtyControls(quantity, isExpanded) {
    const inputMarkup =
      '<input type="number" class="editorial-homepage__cart-qty-input" data-cart-qty-input value="' +
      escapeHtml(String(quantity)) +
      '" min="0" step="1" aria-label="Quantity" inputmode="numeric">';

    const plusMarkup =
      '<button type="button" class="editorial-homepage__cart-qty-btn editorial-homepage__cart-qty-plus" data-cart-qty-plus aria-label="Increase quantity">+</button>';

    if (isExpanded) {
      const minusMarkup =
        quantity > 1
          ? '<button type="button" class="editorial-homepage__cart-qty-btn editorial-homepage__cart-qty-minus" data-cart-qty-minus aria-label="Decrease quantity">&minus;</button>'
          : '';

      return (
        '<div class="editorial-homepage__cart-qty-row" data-cart-qty>' +
        inputMarkup +
        '<div class="editorial-homepage__cart-qty-actions">' +
        minusMarkup +
        plusMarkup +
        '</div>' +
        '</div>'
      );
    }

    return (
      '<div class="editorial-homepage__cart-qty" data-cart-qty>' +
      '<button type="button" class="editorial-homepage__cart-qty-btn editorial-homepage__cart-qty-minus" data-cart-qty-minus aria-label="Decrease quantity"' +
      (quantity <= 1 ? ' disabled' : '') +
      '>&minus;</button>' +
      inputMarkup +
      plusMarkup +
      '</div>'
    );
  }

  /**
   * @param {object} item
   * @param {boolean} isSelected
   * @param {object} labels
   * @param {string} moneyFormat
   * @param {string} currency
   * @returns {string}
   */
  function renderCartItem(item, isSelected, labels, moneyFormat, currency, replaceIconUrl, deleteIconUrl) {
    const title = item.product_title || item.title || '';
    const url = item.url || '#';
    const image = item.image || '';
    const lineKey = item.key || '';
    const quantity = item.quantity || 0;
    const linePrice = item.final_line_price != null ? item.final_line_price : item.line_price || 0;
    const productHandle = extractProductHandle(url);
    const variantId = item.variant_id || item.id || '';

    const imageMarkup = image
      ? '<img class="editorial-homepage__wishlist-image" src="' +
        escapeHtml(image) +
        '" alt="' +
        escapeHtml(title) +
        '" width="44" height="44" loading="lazy">'
      : '<span class="editorial-homepage__wishlist-placeholder" aria-hidden="true"></span>';

    if (!isSelected) {
      return (
        '<article class="editorial-homepage__wishlist-item" data-editorial-cart-item data-line-key="' +
        escapeHtml(lineKey) +
        '" data-cart-select-row aria-pressed="false">' +
        '<div class="editorial-homepage__cart-layout editorial-homepage__cart-layout--compact">' +
        '<div class="editorial-homepage__wishlist-thumb editorial-homepage__wishlist-thumb--static editorial-homepage__cart-cell-thumb" data-cart-thumb aria-label="' +
        escapeHtml(title) +
        '">' +
        imageMarkup +
        '</div>' +
        '<span class="editorial-homepage__wishlist-name editorial-homepage__wishlist-name--static editorial-homepage__cart-cell-title" data-cart-title>' +
        escapeHtml(title) +
        '</span>' +
        renderQtyControls(quantity, false) +
        '</div>' +
        '</article>'
      );
    }

    return (
      '<article class="editorial-homepage__wishlist-item is-selected" data-editorial-cart-item data-line-key="' +
      escapeHtml(lineKey) +
      '" data-variant-id="' +
      escapeHtml(String(variantId)) +
      '" data-product-handle="' +
      escapeHtml(productHandle) +
      '" data-cart-select-row aria-pressed="true">' +
      '<span class="editorial-homepage__cart-select-marker" aria-hidden="true"></span>' +
      '<div class="editorial-homepage__cart-layout editorial-homepage__cart-layout--expanded">' +
      '<div class="editorial-homepage__wishlist-thumb editorial-homepage__wishlist-thumb--static editorial-homepage__cart-cell-thumb" data-cart-thumb aria-label="' +
      escapeHtml(title) +
      '">' +
      imageMarkup +
      '</div>' +
      '<span class="editorial-homepage__wishlist-name editorial-homepage__cart-cell-title" data-cart-title>' +
      escapeHtml(title) +
      '</span>' +
      renderDeleteButton(labels, deleteIconUrl) +
      '<div class="editorial-homepage__cart-detail-block">' +
      renderOptionRows(item, labels, replaceIconUrl) +
      renderQtyControls(quantity, true) +
      '</div>' +
      '<span class="editorial-homepage__wishlist-price editorial-homepage__cart-cell-price">' +
      escapeHtml(formatMoney(linePrice, moneyFormat, currency)) +
      '</span>' +
      '</div>' +
      '</article>'
    );
  }

  /**
   * @param {HTMLElement} panel
   * @param {object[]} items
   */
  function syncCartItemOrder(panel, items) {
    const keys = items
      .map(function (item) {
        return item && item.key ? item.key : '';
      })
      .filter(Boolean);

    if (!Array.isArray(panel._itemOrder) || !panel._itemOrder.length) {
      panel._itemOrder = keys.slice();
      return;
    }

    const keySet = {};
    keys.forEach(function (key) {
      keySet[key] = true;
    });

    panel._itemOrder = panel._itemOrder.filter(function (key) {
      return keySet[key];
    });

    keys.forEach(function (key) {
      if (panel._itemOrder.indexOf(key) === -1) {
        panel._itemOrder.push(key);
      }
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} oldKey
   * @param {string} newKey
   */
  function replaceCartItemOrderKey(panel, oldKey, newKey) {
    if (!newKey) {
      return;
    }

    if (!Array.isArray(panel._itemOrder) || !panel._itemOrder.length) {
      panel._itemOrder = [newKey];
      return;
    }

    const index = panel._itemOrder.indexOf(oldKey);
    if (index !== -1) {
      panel._itemOrder[index] = newKey;
      return;
    }

    if (panel._itemOrder.indexOf(newKey) === -1) {
      panel._itemOrder.push(newKey);
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {object[]} items
   * @returns {object[]}
   */
  function getOrderedCartItems(panel, items) {
    syncCartItemOrder(panel, items);

    const byKey = {};
    items.forEach(function (item) {
      if (item && item.key) {
        byKey[item.key] = item;
      }
    });

    const ordered = [];

    panel._itemOrder.forEach(function (key) {
      if (byKey[key]) {
        ordered.push(byKey[key]);
      }
    });

    return ordered;
  }

  /**
   * @param {HTMLElement} panel
   */
  function scheduleCartPanelViewportSync(panel) {
    const utility = panel.closest('.editorial-homepage__utility--cart');
    if (!(utility instanceof HTMLElement)) {
      return;
    }

    if (window.EditorialPopoverPosition && typeof window.EditorialPopoverPosition.schedule === 'function') {
      window.EditorialPopoverPosition.schedule(utility);
      return;
    }

    if (window.EditorialPopoverPosition && typeof window.EditorialPopoverPosition.syncCartHeight === 'function') {
      window.EditorialPopoverPosition.syncCartHeight(utility);
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {object} cart
   */
  function renderCartPanel(panel, cart) {
    const list = panel.querySelector('[data-editorial-cart-list]');
    const countEl = panel.querySelector('[data-editorial-cart-count]');
    const subtotalEl = panel.querySelector('[data-editorial-cart-subtotal]');
    const footer = panel.querySelector('[data-editorial-cart-footer]');

    if (!list || !countEl || !subtotalEl) return;

    closeCartVariantDropdowns(panel);
    panel._lastCart = cart;

    const emptyText = panel.dataset.emptyText || 'Your cart is empty.';
    const moneyFormat = panel.dataset.moneyFormat || '{{amount}}';
    const currency = cart.currency || 'USD';
    const allItems = Array.isArray(cart.items) ? cart.items : [];
    const items = getOrderedCartItems(panel, allItems);
    const itemCount = items.reduce(function (total, item) {
      return total + (Number(item.quantity) || 0);
    }, 0);

    const labels = {
      edit: panel.dataset.labelEdit || 'Edit',
      remove: panel.dataset.labelRemove || 'Remove',
    };
    const replaceIconUrl = panel.dataset.cartReplaceIcon || '';
    const deleteIconUrl = panel.dataset.cartDeleteIcon || '';

    let selectedKey = getSelectedKey(panel);
    const hasSelected = items.some(function (item) {
      return item.key === selectedKey;
    });

    if (!hasSelected) {
      selectedKey = '';
      setSelectedKey(panel, '');
    }

    countEl.textContent = String(itemCount);
    subtotalEl.textContent = formatMoney(cart.total_price || 0, moneyFormat, currency);

    if (items.length === 0) {
      panel._itemOrder = [];
      setSelectedKey(panel, '');
      list.innerHTML =
        '<div class="editorial-homepage__wishlist-empty" data-editorial-cart-empty>' +
        escapeHtml(emptyText) +
        '</div>';
      if (footer) footer.hidden = true;
      return;
    }

    if (footer) footer.hidden = false;

    list.innerHTML = items
      .map(function (item) {
        return renderCartItem(
          item,
          item.key === selectedKey,
          labels,
          moneyFormat,
          currency,
          replaceIconUrl,
          deleteIconUrl
        );
      })
      .join('');

    requestAnimationFrame(function () {
      scheduleCartPanelViewportSync(panel);
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} lineKey
   * @param {boolean} busy
   */
  function setLineBusy(panel, lineKey, busy) {
    panel.querySelectorAll('[data-editorial-cart-item]').forEach(function (item) {
      if (item.dataset.lineKey === lineKey) {
        item.classList.toggle('is-busy', busy);
      }
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} handle
   * @returns {Promise<object>}
   */
  function fetchProduct(panel, handle) {
    if (!handle) {
      return Promise.reject(new Error('Missing product handle'));
    }

    if (!panel._productCache) {
      panel._productCache = {};
    }

    if (panel._productCache[handle]) {
      return Promise.resolve(panel._productCache[handle]);
    }

    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Product fetch failed');
        }

        return response.json();
      })
      .then(function (product) {
        panel._productCache[handle] = product;
        return product;
      });
  }

  /**
   * @param {object} product
   * @param {number} optionIndex
   * @param {string[]} selectedOptions
   * @returns {string[]}
   */
  function getOptionValues(product, optionIndex, selectedOptions) {
    const values = [];

    product.variants.forEach(function (variant) {
      let matches = true;

      for (let index = 0; index < optionIndex; index += 1) {
        if (variant['option' + (index + 1)] !== selectedOptions[index]) {
          matches = false;
          break;
        }
      }

      if (!matches) {
        return;
      }

      const value = variant['option' + (optionIndex + 1)];
      if (value && values.indexOf(value) === -1) {
        values.push(value);
      }
    });

    return values;
  }

  /**
   * @param {object} product
   * @param {string[]} selectedOptions
   * @returns {object | undefined}
   */
  function findVariantByOptions(product, selectedOptions) {
    return product.variants.find(function (variant) {
      return selectedOptions.every(function (value, index) {
        return variant['option' + (index + 1)] === value;
      });
    });
  }

  /**
   * @param {HTMLElement} row
   */
  function closeOptionRowEditor(row) {
    if (!row) {
      return;
    }

    const dropdown = row.querySelector('[data-cart-variant-dropdown]');
    if (dropdown) {
      closeSingleCartVariantDropdown(dropdown);
    }

    const editor = row.querySelector('[data-cart-variant-editor-row]');
    const display = row.querySelector('[data-cart-option-display]');
    const editBtn = row.querySelector('[data-cart-variant-edit]');

    if (editor) {
      editor.innerHTML = '';
      editor.hidden = true;
    }

    if (display) {
      display.hidden = false;
    }

    if (editBtn) {
      editBtn.setAttribute('aria-expanded', 'false');
      editBtn.classList.remove('is-active');
    }

    row.classList.remove('is-editing');
  }

  /**
   * @param {HTMLElement} item
   */
  function closeAllOptionRowEditors(item) {
    item.querySelectorAll('[data-cart-option-row]').forEach(closeOptionRowEditor);
    delete item.dataset.variantEditorOpen;
  }

  /**
   * @param {HTMLElement} row
   * @param {object} product
   * @param {number} optionIndex
   * @param {string[]} selectedOptions
   * @param {string} [optionNameHint]
   */
  function renderSingleOptionEditor(row, product, optionIndex, selectedOptions, optionNameHint) {
    const editor = row.querySelector('[data-cart-variant-editor-row]');
    const display = row.querySelector('[data-cart-option-display]');
    const editBtn = row.querySelector('[data-cart-variant-edit]');

    if (!editor) {
      return;
    }

    const optionName =
      (optionNameHint && String(optionNameHint).trim()) || getProductOptionName(product, optionIndex);
    const values = getOptionValues(product, optionIndex, selectedOptions);
    const currentValue = selectedOptions[optionIndex] || values[0] || '';

    if (!values.length) {
      editor.innerHTML =
        '<div class="editorial-homepage__cart-variant-editor-card">' +
        '<p class="editorial-homepage__cart-variant-editor-note">' +
        escapeHtml('No options available.') +
        '</p>' +
        '</div>';
    } else {
      editor.innerHTML =
        '<div class="editorial-homepage__cart-variant-editor-card">' +
        (optionName
          ? '<span class="editorial-homepage__cart-variant-label">' + escapeHtml(optionName) + '</span>'
          : '') +
        renderOptionSelect(values, currentValue, optionIndex, optionName) +
        '</div>';
    }

    editor.hidden = false;

    if (display) {
      display.hidden = true;
    }

    if (editBtn) {
      editBtn.setAttribute('aria-expanded', 'true');
      editBtn.classList.add('is-active');
    }

    row.classList.add('is-editing');
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} item
   * @param {number} optionIndex
   * @param {HTMLElement} row
   * @returns {Promise<void>}
   */
  function openOptionRowEditor(panel, item, optionIndex, row) {
    const handle = item.dataset.productHandle || '';
    const cartItem =
      panel._lastCart && Array.isArray(panel._lastCart.items)
        ? panel._lastCart.items.find(function (entry) {
            return entry.key === item.dataset.lineKey;
          })
        : null;

    if (!handle || !cartItem || !row) {
      return Promise.resolve();
    }

    if (panel._activeVariantEditorItem && panel._activeVariantEditorItem !== item) {
      closeAllOptionRowEditors(panel._activeVariantEditorItem);
    }

    closeAllOptionRowEditors(item);
    panel._activeVariantEditorItem = item;
    item.dataset.variantEditorOpen = 'true';
    panel._activeOptionRow = row;

    const selectedOptions = getItemOptionValues(cartItem);

    return fetchProduct(panel, handle)
      .then(function (product) {
        if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
          const editor = row.querySelector('[data-cart-variant-editor-row]');
          if (editor) {
            editor.innerHTML =
              '<div class="editorial-homepage__cart-variant-editor-card">' +
              '<p class="editorial-homepage__cart-variant-editor-note">' +
              escapeHtml('No options for this item.') +
              '</p>' +
              '</div>';
            editor.hidden = false;
            row.querySelector('[data-cart-option-display]').hidden = true;
          }

          return;
        }

        const optionsMeta = getItemOptionsWithMeta(cartItem);
        const optionNameHint = optionsMeta[optionIndex] ? optionsMeta[optionIndex].name : '';

        renderSingleOptionEditor(row, product, optionIndex, selectedOptions, optionNameHint);
      })
      .catch(function () {
        closeOptionRowEditor(row);
      });
  }

  /**
   * @param {HTMLElement} row
   * @param {object} product
   * @param {number} optionIndex
   * @param {string[]} selectedOptions
   */
  function refreshSingleOptionSelect(row, product, optionIndex, selectedOptions) {
    const select = row.querySelector('[data-cart-option-select]');
    if (!select) {
      return;
    }

    const values = getOptionValues(product, optionIndex, selectedOptions);
    const currentValue = selectedOptions[optionIndex] || values[0] || '';
    const nextValue = values.indexOf(currentValue) !== -1 ? currentValue : values[0] || '';

    select.innerHTML = values
      .map(function (value) {
        return (
          '<option value="' +
          escapeHtml(value) +
          '"' +
          (value === nextValue ? ' selected' : '') +
          '>' +
          escapeHtml(value) +
          '</option>'
        );
      })
      .join('');

    select.value = nextValue;

    const dropdown = row.querySelector('[data-cart-variant-dropdown]');
    const valueEl = row.querySelector('[data-cart-dropdown-value]');
    const menu = dropdown ? findDropdownMenu(dropdown) : row.querySelector('[data-cart-dropdown-menu]');

    if (valueEl) {
      valueEl.textContent = nextValue;
    }

    if (menu) {
      menu.innerHTML = renderDropdownMenuOptions(values, nextValue);
    }

    selectedOptions[optionIndex] = nextValue;
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} item
   * @param {number} optionIndex
   */
  function handleVariantSelectChange(panel, item, optionIndex) {
    const row = item.querySelector('[data-cart-option-row][data-option-index="' + optionIndex + '"]');
    const handle = item.dataset.productHandle || '';
    const lineKey = item.dataset.lineKey || '';
    const currentVariantId = String(item.dataset.variantId || '');

    if (!row || !handle || !lineKey || !panel._lastCart) {
      return;
    }

    const cachedProduct = panel._productCache && panel._productCache[handle];

    if (!cachedProduct) {
      fetchProduct(panel, handle).then(function () {
        handleVariantSelectChange(panel, item, optionIndex);
      });
      return;
    }

    const product = cachedProduct;

    const cartItem = panel._lastCart.items.find(function (entry) {
      return entry.key === lineKey;
    });

    if (!cartItem) {
      return;
    }

    const selectedOptions = getItemOptionValues(cartItem);
    const select = row.querySelector('[data-cart-option-select]');

    if (!select) {
      return;
    }

    selectedOptions[optionIndex] = select.value;
    refreshSingleOptionSelect(row, product, optionIndex, selectedOptions);

    const nextVariant = findVariantByOptions(product, selectedOptions);

    if (!nextVariant || String(nextVariant.id) === currentVariantId) {
      return;
    }

    const quantity = cartItem.quantity || 1;
    const selectedSnapshot = selectedOptions.slice();

    applyOptimisticVariantUpdate(panel, item, cartItem, product, nextVariant, selectedSnapshot);
    swapCartVariant(panel, lineKey, nextVariant.id, quantity, { optimistic: true });
  }

  /**
   * @param {HTMLElement} item
   * @param {string[]} selectedOptions
   */
  function updateOptionDisplayValues(item, selectedOptions) {
    item.querySelectorAll('[data-cart-option-row]').forEach(function (row) {
      const optionIndex = parseInt(row.dataset.optionIndex, 10);
      if (Number.isNaN(optionIndex)) {
        return;
      }

      const value = selectedOptions[optionIndex] || '';
      const valueEl = row.querySelector('.editorial-homepage__cart-option-value');
      if (valueEl) {
        valueEl.textContent = formatOptionHash(value);
      }
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} item
   * @param {object} cartItem
   * @param {object} product
   * @param {object} nextVariant
   * @param {string[]} selectedOptions
   */
  function applyOptimisticVariantUpdate(panel, item, cartItem, product, nextVariant, selectedOptions) {
    item.dataset.variantId = String(nextVariant.id);

    cartItem.variant_id = nextVariant.id;
    cartItem.variant_title = nextVariant.title || cartItem.variant_title;
    if (Array.isArray(product.options)) {
      cartItem.options_with_values = product.options.map(function (option, index) {
        return {
          name: option.name || option,
          value: selectedOptions[index] || '',
        };
      });
    }

    const workingOptions = selectedOptions.slice();
    item.querySelectorAll('[data-cart-option-row]').forEach(function (row) {
      const optionIndex = parseInt(row.dataset.optionIndex, 10);
      if (Number.isNaN(optionIndex)) {
        return;
      }

      refreshSingleOptionSelect(row, product, optionIndex, workingOptions);
    });

    updateOptionDisplayValues(item, workingOptions);
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} lineKey
   * @param {number} newVariantId
   * @param {number} quantity
   * @param {{ optimistic?: boolean }} [options]
   * @returns {Promise<void>}
   */
  function swapCartVariant(panel, lineKey, newVariantId, quantity, options) {
    const changeUrl = panel.dataset.cartChangeUrl || '/cart/change.js';
    const safeQty = Math.max(1, quantity);
    const optimistic = Boolean(options && options.optimistic);

    if (!optimistic) {
      setLineBusy(panel, lineKey, true);
      setPanelLoading(panel, true);
    }

    return changeCartLine(changeUrl, lineKey, 0)
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: newVariantId,
            quantity: safeQty,
          }),
        }).then(function (response) {
          return response.json().then(function (payload) {
            if (!response.ok || payload.status) {
              throw new Error(payload.description || payload.message || 'Cart update failed');
            }

            return payload;
          });
        });
      })
      .then(function (addedLine) {
        const newLineKey = addedLine && addedLine.key ? addedLine.key : '';

        if (newLineKey) {
          replaceCartItemOrderKey(panel, lineKey, newLineKey);
        }

        return fetchCart().then(function (cart) {
          return {
            cart: cart,
            newLineKey: newLineKey,
          };
        });
      })
      .then(function (result) {
        const cart = result.cart;
        const newLineKey = result.newLineKey;
        const nextItem = newLineKey
          ? cart.items.find(function (entry) {
              return entry.key === newLineKey;
            })
          : cart.items.find(function (entry) {
              return String(entry.variant_id || entry.id) === String(newVariantId);
            });

        if (panel._activeVariantEditorItem) {
          closeAllOptionRowEditors(panel._activeVariantEditorItem);
          panel._activeVariantEditorItem = null;
          panel._activeOptionRow = null;
        }

        if (nextItem && nextItem.key) {
          setSelectedKey(panel, nextItem.key);
        } else if (getSelectedKey(panel) === lineKey) {
          setSelectedKey(panel, '');
        }

        renderCartPanel(panel, cart);
        dispatchCartUpdate(cart);
      })
      .catch(function () {
        return fetchCart().then(function (cart) {
          renderCartPanel(panel, cart);
        });
      })
      .finally(function () {
        if (!optimistic) {
          setLineBusy(panel, lineKey, false);
          setPanelLoading(panel, false);
        }
      });
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} lineKey
   * @param {number} quantity
   * @returns {Promise<void>}
   */
  function updateLineQuantity(panel, lineKey, quantity) {
    const changeUrl = panel.dataset.cartChangeUrl || '/cart/change.js';
    const safeQty = Math.max(0, quantity);

    setLineBusy(panel, lineKey, true);
    setPanelLoading(panel, true);

    const updateRequest = changeCartLine(changeUrl, lineKey, safeQty);

    return updateRequest
      .then(function (cart) {
        if (safeQty === 0) {
          hideCartPanelNotice(panel);

          if (Array.isArray(panel._itemOrder)) {
            panel._itemOrder = panel._itemOrder.filter(function (key) {
              return key !== lineKey;
            });
          }

          if (getSelectedKey(panel) === lineKey) {
            setSelectedKey(panel, '');
          }
        } else {
          const limitMessage = getInventoryLimitMessage(panel, lineKey, safeQty, cart);

          if (limitMessage) {
            showCartPanelNotice(panel, limitMessage);
          } else {
            hideCartPanelNotice(panel);
          }
        }

        renderCartPanel(panel, cart);
        dispatchCartUpdate(cart);
      })
      .catch(function (error) {
        return fetchCart().then(function (cart) {
          const line =
            cart && Array.isArray(cart.items)
              ? cart.items.find(function (entry) {
                  return entry.key === lineKey;
                })
              : null;
          const availableQty = line ? line.quantity : undefined;

          showCartPanelNotice(panel, getInventoryErrorMessage(panel, error, availableQty));
          renderCartPanel(panel, cart);
        });
      })
      .finally(function () {
        setLineBusy(panel, lineKey, false);
        setPanelLoading(panel, false);
      });
  }

  /**
   * @param {HTMLElement} panel
   * @param {object} [cart]
   * @param {{ silent?: boolean }} [options]
   * @returns {Promise<void>}
   */
  function refreshCartPanel(panel, cart, options) {
    const silent = Boolean(options && options.silent);

    if (!silent) {
      setPanelLoading(panel, true);
    }

    const finish = function (cartData) {
      renderCartPanel(panel, cartData);
      if (!silent) {
        setPanelLoading(panel, false);
      }
    };

    if (cart) {
      finish(cart);
      return Promise.resolve();
    }

    return fetchCart()
      .then(finish)
      .catch(function () {
        if (!silent) {
          setPanelLoading(panel, false);
        }
      });
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} item
   */
  function toggleItemSelection(panel, item) {
    const lineKey = item.dataset.lineKey || '';
    if (!lineKey) return;

    if (panel._activeVariantEditorItem && panel._activeVariantEditorItem !== item) {
      closeAllOptionRowEditors(panel._activeVariantEditorItem);
      panel._activeVariantEditorItem = null;
      panel._activeOptionRow = null;
    }

    const nextKey = getSelectedKey(panel) === lineKey ? '' : lineKey;
    setSelectedKey(panel, nextKey);

    if (nextKey !== lineKey && item.dataset.variantEditorOpen === 'true') {
      closeAllOptionRowEditors(item);
      panel._activeVariantEditorItem = null;
      panel._activeOptionRow = null;
    }

    if (panel._lastCart) {
      renderCartPanel(panel, panel._lastCart);
      return;
    }

    fetchCart().then(function (cart) {
      renderCartPanel(panel, cart);
    });
  }

  /**
   * @param {HTMLElement} panel
   */
  function bindCheckoutNavigation(panel) {
    const checkoutUrl = panel.dataset.cartCheckoutUrl || '/checkout';

    panel.addEventListener(
      'click',
      function (event) {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const checkoutCta = target.closest('[data-cart-checkout]');
        if (!checkoutCta || !panel.contains(checkoutCta)) return;

        event.preventDefault();
        event.stopPropagation();

        const href = checkoutCta.getAttribute('href') || checkoutUrl;
        if (href) {
          window.location.assign(href);
        }
      },
      true
    );
  }

  /**
   * @param {WheelEvent} event
   * @param {HTMLElement} list
   * @returns {number}
   */
  function getWheelDeltaY(event, list) {
    let delta = event.deltaY;

    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
      delta *= 16;
    } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      delta *= list.clientHeight || window.innerHeight;
    }

    return delta;
  }

  /**
   * @param {HTMLElement} list
   * @param {number} deltaY
   * @returns {boolean}
   */
  function scrollCartListByDelta(list, deltaY) {
    const maxScroll = list.scrollHeight - list.clientHeight;
    if (maxScroll <= 0) {
      return false;
    }

    const nextTop = Math.max(0, Math.min(maxScroll, list.scrollTop + deltaY));
    if (nextTop === list.scrollTop) {
      return false;
    }

    list.scrollTop = nextTop;
    return true;
  }

  /**
   * @param {HTMLElement} panel
   */
  function bindCartListWheel(panel) {
    const list = panel.querySelector('[data-editorial-cart-list]');
    if (!(list instanceof HTMLElement) || list.dataset.cartWheelBound === 'true') {
      return;
    }

    list.dataset.cartWheelBound = 'true';

    list.addEventListener(
      'wheel',
      function (event) {
        if (!(event instanceof WheelEvent)) {
          return;
        }

        const target = event.target;
        if (target instanceof Element && target.closest('[data-cart-dropdown-menu]')) {
          return;
        }

        const maxScroll = list.scrollHeight - list.clientHeight;
        if (maxScroll <= 0) {
          return;
        }

        const delta = getWheelDeltaY(event, list);
        if (!delta) {
          return;
        }

        const before = list.scrollTop;
        const canScrollDown = delta > 0 && before < maxScroll;
        const canScrollUp = delta < 0 && before > 0;

        if (!canScrollDown && !canScrollUp) {
          return;
        }

        scrollCartListByDelta(list, delta);
        event.preventDefault();
        event.stopPropagation();
      },
      { passive: false }
    );

    let touchStartY = 0;

    list.addEventListener(
      'touchstart',
      function (event) {
        const touch = event.touches && event.touches[0];
        if (touch) {
          touchStartY = touch.clientY;
        }
      },
      { passive: true }
    );

    list.addEventListener(
      'touchmove',
      function (event) {
        const target = event.target;
        if (target instanceof Element && target.closest('[data-cart-dropdown-menu]')) {
          return;
        }

        const touch = event.touches && event.touches[0];
        if (!touch) {
          return;
        }

        const deltaY = touchStartY - touch.clientY;
        touchStartY = touch.clientY;

        if (scrollCartListByDelta(list, deltaY)) {
          event.preventDefault();
        }
      },
      { passive: false }
    );
  }

  function bindCartPanelInteractions(panel) {
    if (panel.dataset.cartInteractionsBound === 'true') return;
    panel.dataset.cartInteractionsBound = 'true';

    bindCartListWheel(panel);
    bindCheckoutNavigation(panel);

    panel.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-cart-dropdown-trigger]')) {
        event.preventDefault();
        event.stopPropagation();

        const dropdown = target.closest('[data-cart-variant-dropdown]');
        if (!dropdown || !panel.contains(dropdown)) {
          return;
        }

        toggleCartVariantDropdown(dropdown);
        return;
      }

      const item = target.closest('[data-editorial-cart-item]');
      if (!item || !panel.contains(item)) return;

      const lineKey = item.dataset.lineKey || '';
      if (!lineKey) return;

      if (target.closest('[data-cart-qty-minus]')) {
        event.preventDefault();
        event.stopPropagation();
        const input = item.querySelector('[data-cart-qty-input]');
        const current = parseInt(input && input.value, 10) || 0;
        updateLineQuantity(panel, lineKey, Math.max(0, current - 1));
        return;
      }

      if (target.closest('[data-cart-qty-plus]')) {
        event.preventDefault();
        event.stopPropagation();
        const input = item.querySelector('[data-cart-qty-input]');
        const current = parseInt(input && input.value, 10) || 0;
        updateLineQuantity(panel, lineKey, current + 1);
        return;
      }

      if (target.closest('[data-cart-remove]')) {
        event.preventDefault();
        event.stopPropagation();
        updateLineQuantity(panel, lineKey, 0);
        return;
      }

      if (target.closest('[data-cart-variant-edit]')) {
        event.preventDefault();
        event.stopPropagation();

        closeCartVariantDropdowns(panel);

        const editBtn = target.closest('[data-cart-variant-edit]');
        const optionIndex = parseInt(editBtn && editBtn.dataset.optionIndex, 10);
        const row =
          editBtn && editBtn.closest('[data-cart-option-row]')
            ? editBtn.closest('[data-cart-option-row]')
            : null;

        if (!row || Number.isNaN(optionIndex)) {
          return;
        }

        if (row.classList.contains('is-editing')) {
          closeOptionRowEditor(row);

          if (!item.querySelector('[data-cart-option-row].is-editing')) {
            delete item.dataset.variantEditorOpen;
            panel._activeVariantEditorItem = null;
            panel._activeOptionRow = null;
          }
        } else {
          openOptionRowEditor(panel, item, optionIndex, row);
        }

        return;
      }

      if (
        target.closest('[data-cart-qty]') ||
        target.closest('[data-cart-qty-input]') ||
        target.closest('[data-cart-variant-edit]') ||
        target.closest('[data-cart-variant-dropdown]') ||
        target.closest('[data-cart-dropdown-menu]') ||
        target.closest('[data-cart-variant-editor-row]') ||
        target.closest('[data-cart-remove]')
      ) {
        return;
      }

      if (!target.closest('[data-cart-select-row]')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      toggleItemSelection(panel, item);
    });

    panel.addEventListener('change', function (event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('[data-cart-option-select]')) {
        const item = target.closest('[data-editorial-cart-item]');
        if (!item || !panel.contains(item)) return;

        const optionIndex = parseInt(target.dataset.optionIndex, 10);
        if (Number.isNaN(optionIndex)) {
          return;
        }

        event.stopPropagation();
        handleVariantSelectChange(panel, item, optionIndex);
        return;
      }

      if (!(target instanceof HTMLInputElement) || !target.matches('[data-cart-qty-input]')) return;

      const item = target.closest('[data-editorial-cart-item]');
      if (!item) return;

      const lineKey = item.dataset.lineKey || '';
      const quantity = parseInt(target.value, 10);

      if (!lineKey || !Number.isFinite(quantity)) return;

      updateLineQuantity(panel, lineKey, quantity);
    });
  }

  /**
   * @param {HTMLElement} panel
   */
  function bindCartPanel(panel) {
    if (panel.dataset.cartPanelInit === 'true') return;
    panel.dataset.cartPanelInit = 'true';

    ensureCartDropdownDocumentClose();
    bindCartPanelInteractions(panel);

    const utility = panel.closest('.editorial-homepage__utility--cart');

    const scheduleRefresh = function (cart) {
      refreshCartPanel(panel, cart);
    };

    if (utility) {
      utility.addEventListener('mouseenter', function () {
        if (panel.dataset.cartHoverRefresh === 'true') {
          return;
        }

        panel.dataset.cartHoverRefresh = 'true';
        scheduleRefresh();
      });

      utility.addEventListener('mouseleave', function (event) {
        if (utility.contains(event.relatedTarget)) {
          return;
        }

        delete panel.dataset.cartHoverRefresh;
      });
    }

    document.addEventListener('cart:update', function (event) {
      if (event.detail && event.detail.data && event.detail.data.source === 'editorial-cart-panel') {
        return;
      }

      const resource = event.detail && event.detail.resource;
      scheduleRefresh(resource || undefined);
    });

    scheduleRefresh();
  }

  function initCartPanels() {
    document.querySelectorAll('[data-editorial-cart-panel]').forEach(bindCartPanel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartPanels);
  } else {
    initCartPanels();
  }

  document.addEventListener('shopify:section:load', initCartPanels);
})();
